import { FETCH_DETAILS_START, FETCH_DETAILS_SUCCESS, FETCH_DETAILS_FAIL } from './actionTypes';
import fetchData from './fetchData';
import { checkTokenTimeout } from './condition';
import { fetchListSuccess } from './list';
import { setGlobalState } from './index';

/* * * * * * * * * * * * * * *
* ACTIVE ACTION TYPE METHODS *
* * * * * * * * * * * * * *  */
// action type methods return current active action type that is determined by the state of the fetch requests.
// Also these methods pass data passed from user methods to Redux reducers to update states
export const fetchDetailsStart = (identifier) => {
    return {
        type: FETCH_DETAILS_START,
        identifier: identifier
    };
};

export const fetchDetailsSuccess = (identifier, data = {}) => {
    return {
        type: FETCH_DETAILS_SUCCESS,
        identifier: identifier,
        data: data
    };
};

export const fetchDetailsFail = (identifier, error = 'Error message missing. Please contact site administrator.') => {
    return {
        type: FETCH_DETAILS_FAIL,
        identifier: identifier,
        error: error
    };
};

/* * * * * * * * * * * *  *
* USER ACCESSIBLE METHODS *
* * * * * * * * * * * * * */
// User accessible methods are exported and can be accessed across the whole project.
// These methods execute fetch calls and dispatch correct method that updates active action type according the state of the request

/* * * * * * * *
* USER DETAILS *
* * * * * * *  */
export const login = (email = null, password = null, remember = false) => {
    const tokenBodyData = !!email && !!password ? {
        userName: email,
        password: password,
        remember: remember
    } : {};

    return async dispatch => {
        dispatch(fetchDetailsStart('user'));
        try {
            // get token and user data
            await fetchData('POST', 'token', tokenBodyData);
            const userData = await fetchData('GET', 'users/me');
            localStorage.setItem('userData', JSON.stringify(userData));

            dispatch(checkTokenTimeout());
            dispatch(fetchDetailsSuccess('user', userData));
        } catch (error) {
            if(error === 'email_unverified') {
                dispatch(setGlobalState('verifyEmail', email));
                dispatch(setGlobalState('isLoginOpen', false));
                dispatch(fetchDetailsFail('user', null));
                dispatch(setGlobalState('userRedirectPath', 'verification'));
            } else {
                dispatch(fetchDetailsFail('user', error));
            }
        }
    };
};

/* * * * * * *
* FETCH CART *
* * * * * *  */
export const fetchCart = (paymentAccountId = null, paymentMethodId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('cart'));
        try {
            const data = await fetchData('GET', `paymentAccounts/${paymentAccountId}/cart`);
            if (data.cartItems.length) {
                dispatch(updateCartPricing(paymentAccountId, paymentMethodId));
            } else {
                dispatch(fetchDetailsSuccess('cartPricing', null));
            }
            dispatch(fetchDetailsSuccess('cart', data));
        } catch (error) {
            dispatch(fetchDetailsFail('cart', error));
        }
    };
};


/* * * * * * * *
* CART PRICING *
* * * * * * *  */
export const updateCartPricing = (paymentAccountId = null, paymentMethodId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('cartPricing'));
        try {
            const data = await fetchData('GET', `paymentAccounts/${paymentAccountId}/cart/price${paymentMethodId ? `?paymentOptionId=${paymentMethodId}` : ''}`);
            dispatch(fetchDetailsSuccess('cartPricing', data));
        } catch (error) {
            dispatch(fetchDetailsFail('cartPricing', error));
        }
    };
};

/* * * * * * * *  *
* VOUCHER METHODS *
* * * * * * * * * */
export const applyVoucher = (paymentAccountId = null, voucherCode = {}, paymentMethodId = null, cartData) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('addVoucher'));
        try {
            const data = await fetchData('POST', `paymentAccounts/${paymentAccountId}/cart/applyVoucher`, voucherCode);
            const updatedCartItems = cartData.cartItems.map(item => item.id === data.id ? data : item);
            const updatedCartData = { ...cartData, ...{ cartItems: updatedCartItems } };
            dispatch(fetchDetailsSuccess('cart', updatedCartData));
            dispatch(updateCartPricing(paymentAccountId, paymentMethodId));
            dispatch(fetchDetailsSuccess('addVoucher', data));
        } catch (error) {
            dispatch(fetchDetailsFail('addVoucher', error));
        }
    };
};


/* * * * * * *  *
* CART CHECKOUT *
* * * * * * * * */
export const checkoutCart = (paymentAccountId = null, paymentMethodId = null, redirectUrl = null) => {
    const checkoutBody = paymentMethodId && redirectUrl ? {
        paymentOptionId: paymentMethodId,
        paymentRedirectUri: redirectUrl
    } : {};

    return async dispatch => {
        dispatch(fetchDetailsStart('checkout'));
        try {
            const data = await fetchData('POST', `paymentAccounts/${paymentAccountId}/cart/checkout`, checkoutBody);
            dispatch(fetchDetailsSuccess('checkout', data));
        } catch (error) {
            dispatch(fetchDetailsFail('checkout', error));
        }
    };
};

/* * * * * * * * * * * *  *
* PAYMENT ACCOUNT DETAILS *
* * * * * * * * * * * * * */
export const fetchPaymentAccountDetails = (acountId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('paymentAccount'));
        try {
            const data = await fetchData('GET', `paymentAccounts/${acountId}`);
            dispatch(fetchDetailsSuccess('paymentAccount', data));
        } catch (error) {
            dispatch(fetchDetailsFail('paymentAccount', error));
        }

    };
};

/* * * * * * *  *
* ITEMS DETAILS *
* * * * * * * * */
export const fetchItemDetails = (id = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('item'));
        try {
            const data = await fetchData('GET', `items/${id}`);
            dispatch(fetchDetailsSuccess('item', data));
        } catch (error) {
            dispatch(fetchDetailsFail('item', error));
        }
    };
};

/* * * * * * * * * * * * * *
* CURRENT LOCATION DETAILS *
* * * * * * * * * * * * *  */
export const fetchCurrentLocation = () => {
    return dispatch => {
        dispatch(fetchDetailsStart('currentLocation'));
        navigator.geolocation.getCurrentPosition(
            location => dispatch(fetchDetailsSuccess('currentLocation', { id: 'CURRENT_LOCATION', value: 'CURRENT_LOCATION', lat: location.coords.latitude, lng: location.coords.longitude })),
            error => dispatch(fetchDetailsFail('currentLocation', error.message)),
            {
                enableHighAccuracy: true,
                timeout: 15000,     // 15 seconds
                maximumAge: 60000   // 1 minute
            }
        );
    };
};

/* * * * * * *  *
* PRICE DETAILS *
* * * * * * * * */
export const fetchPriceDetails = (itemId = null, date = { start: null, end: null }) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('price'));
        try {
            const data = await fetchData('GET', `items/${itemId}/price?periodStart=${date.start}&periodEnd=${date.end}`);
            dispatch(fetchDetailsSuccess('price', data));
        } catch (error) {
            dispatch(fetchDetailsFail('price', error));
        }
    };
};

/* * * * * * * *  *
* CONTACT DETAILS *
* * * * * * * * * */
export const fetchContactDetails = (companyId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('contact'));
        try {
            const data = await fetchData('GET', `items/${companyId}/contact`);
            dispatch(fetchDetailsSuccess('contact', data));
        } catch (error) {
            dispatch(fetchDetailsFail('contact', error));
        }
    };
};

/* * * * * * * * * * * *  *
* SEARCH LOCATION DETAILS *
* * * * * * * * * * * * * */
export const fetchSearchLocationDetails = (locationId) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('searchLocation'));
        try {
            const data = await fetchData('GET', `geolocations/details?placeid=${locationId}`);
            dispatch(fetchDetailsSuccess('searchLocation', { id: locationId, value: data.formatted_address, ...data.geometry.location }));
        } catch (error) {
            dispatch(fetchDetailsFail('searchLocation', error));
        }
    };
};

/* * * * * * * * * *  *
* RESERVATION DETAILS *
* * * * * * * * * * * */
export const fetchReservationDetails = (reservationId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('reservation'));
        try {
            const data = await fetchData('GET', `reservations/${reservationId}`);
            dispatch(fetchDetailsSuccess('reservation', data));
        } catch (error) {
            dispatch(fetchDetailsFail('reservation', error));
        }
    };
};

export const cancelReservation = (reservationId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('cancelReservation'));
        try {
            const data = await fetchData('POST', `reservations/${reservationId}/cancel`);
            dispatch(fetchDetailsSuccess('reservation', data));
            dispatch(fetchDetailsSuccess('cancelReservation', true));
        } catch (error) {
            dispatch(fetchDetailsFail('cancelReservation', error));
        }
    };
};


/* * * * * * *  *
* ORDER DETAILS *
* * * * * * * * */
export const fetchOrderDetails = (orderId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('order'));
        try {
            const data = await fetchData('GET', `orders/${orderId}`);
            dispatch(fetchDetailsSuccess('order', data));
        } catch (error) {
            dispatch(fetchDetailsFail('order', error));
        }
    };
};

/* * * * * * * * * * * * * * * *
* DIRECT DEBIT PAYMENT METHODS *
* * * * * * * * * * * * * * *  */
export const addDirectDebitDetails = (paymentMethodId = null, redirectUrl = null, description = null) => {
    const directDebitBody = paymentMethodId && redirectUrl && description ? {
        paymentMethodId: paymentMethodId,
        description: description,
        redirectUri: redirectUrl
    } : {};

    return async dispatch => {
        dispatch(fetchDetailsStart('addDirectDebit'));
        try {
            const data = await fetchData('POST', 'directDebitPaymentMethods', directDebitBody);
            dispatch(fetchDetailsSuccess('addDirectDebit', data));
        } catch (error) {
            dispatch(fetchDetailsFail('addDirectDebit', error));
        }
    };
};

export const updateDirectDebitDetails = (paymentMethodId = null, description = null, directDebits = []) => {
    const directDebitBody = description ? {
        description: description
    } : {};

    return async dispatch => {
        dispatch(fetchDetailsStart('updateDirectDebit'));
        try {
            const data = await fetchData('PUT', `directDebitPaymentMethods/${paymentMethodId}`, directDebitBody);
            const updatedDirectDebits = directDebits.map(debit => debit.id === data.id ? data : debit);
            dispatch(fetchListSuccess('directDebits', updatedDirectDebits));
            dispatch(fetchDetailsSuccess('updateDirectDebit', data));
        } catch (error) {
            dispatch(fetchDetailsFail('updateDirectDebit', error));
        }
    };
};

/* * * * * * * * *  *
* COMMUNITY DETAILS *
* * * * * * * * * * */
export const fetchCommunityDetails = () => {
    return async dispatch => {
        dispatch(fetchDetailsStart('community'));
        try {
            const data = await fetchData('GET', 'community');
            localStorage.removeItem('communityId');
            localStorage.setItem('communityId', data.id);
            if(data.name !== '360') {
                document.title = data.name.slice(0,1).toUpperCase()+data.name.slice(1);
            }
            dispatch(fetchCommunityContactDetails(data.id));
            if (data.companyReference) {
                dispatch(fetchCommunityCompanyDetails(data.companyReference.id));
            }
            dispatch(fetchDetailsSuccess('community', data));
        } catch (error) {
            dispatch(fetchDetailsFail('community', error));
        }

    };
};

export const fetchCommunityContactDetails = (communityId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('communityContact'));
        try {
            const data = await fetchData('GET', `communities/${communityId}/contact`);
            dispatch(fetchDetailsSuccess('communityContact', data));
        } catch (error) {
            dispatch(fetchDetailsFail('communityContact', error));
        }

    };
};

export const fetchCommunityCompanyDetails = (companyId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('communityCompany'));
        try {
            const data = await fetchData('GET', `companies/${companyId}`);
            dispatch(fetchDetailsSuccess('communityCompany', data));
        } catch (error) {
            dispatch(fetchDetailsFail('communityCompany', error));
        }

    };
};

/* * * * * * *
 * INVOICES  *
 * * * * * * */
export const fetchInvoiceDetails = (invoiceId = null) => {
    return async dispatch => {
        dispatch(fetchDetailsStart('invoice'));
        try {
            const data = await fetchData('GET', `invoices/${invoiceId}`);
            dispatch(fetchDetailsSuccess('invoice', data));
        } catch (error) {
            dispatch(fetchDetailsFail('invoice', error));
        }

    };
};



// WEBPACK FOOTER //
// ./src/store/actions/details.js